"use client";

import { useMemo } from "react";

import { liteClient as algoliasearch } from "algoliasearch/lite";
import { Activity, AlertCircle, Calendar, FileText, Pill, Search, Stethoscope } from "lucide-react";
import {
  Configure,
  Highlight,
  Hits,
  InstantSearch,
  Pagination,
  RefinementList,
  SearchBox,
  useInstantSearch,
  useStats,
} from "react-instantsearch";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { algoliaConfig, getPatientTimelineIndex } from "@/lib/config/algolia-config";
import { cn } from "@/lib/utils";

// Initialize the Algolia search client
const searchClient = algoliasearch(algoliaConfig.appId, algoliaConfig.searchKey);

// Type icons mapping
const typeIcons: Record<string, React.ElementType> = {
  encounter: Stethoscope,
  medication: Pill,
  condition: AlertCircle,
  lab: Activity,
  imaging: FileText,
  procedure: Activity,
  default: FileText,
};

// Type colors mapping
const typeColors: Record<string, string> = {
  encounter: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  medication: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  condition: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  lab: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  imaging: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  procedure: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  default: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
};

// Custom Hit component for timeline results (patient-specific index - no patient fields)
function TimelineHit({ hit }: { hit: any }) {
  const type = hit.type?.toLowerCase() || "default";
  const IconComponent = typeIcons[type] || typeIcons.default;
  const colorClass = typeColors[type] || typeColors.default;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex w-full max-w-full items-start gap-3 rounded-lg border bg-card p-4 transition-all hover:bg-accent/50 hover:shadow-sm">
      <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", colorClass)}>
        <IconComponent className="h-5 w-5" />
      </div>
      <div className="w-full min-w-0 flex-1 space-y-1 overflow-hidden">
        <div className="flex w-full items-start justify-between gap-2">
          <h4 className="overflow-wrap-anywhere min-w-0 flex-1 break-words font-medium">
            <Highlight
              attribute="title"
              hit={hit}
              classNames={{
                root: "break-words overflow-wrap-anywhere",
                highlighted: "bg-yellow-200 dark:bg-yellow-900/50 font-semibold rounded px-0.5",
              }}
            />
          </h4>
          <Badge variant="outline" className="ml-2 shrink-0 text-xs capitalize">
            {hit.type}
          </Badge>
        </div>
        {hit.description && (
          <p className="overflow-wrap-anywhere break-words text-sm text-muted-foreground">
            <Highlight
              attribute="description"
              hit={hit}
              classNames={{
                root: "break-words overflow-wrap-anywhere",
                highlighted: "bg-yellow-200 dark:bg-yellow-900/50 font-semibold rounded px-0.5",
              }}
            />
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3 shrink-0" />
          <span className="whitespace-nowrap">{formatDate(hit.date)}</span>
          {hit.provider && (
            <>
              <span className="shrink-0">•</span>
              <span className="max-w-[200px] truncate">{hit.provider}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Stats component
function SearchStats() {
  const { nbHits, processingTimeMS } = useStats();

  return (
    <div className="text-sm text-muted-foreground">
      {nbHits.toLocaleString()} results found in {processingTimeMS}ms
    </div>
  );
}

// No results component
function NoResults() {
  const { indexUiState } = useInstantSearch();

  return (
    <div className="py-8 text-center">
      <Search className="mx-auto h-12 w-12 text-muted-foreground/50" />
      <h3 className="mt-4 text-lg font-medium">No results found</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        {indexUiState.query
          ? `No timeline events found for "${indexUiState.query}"`
          : "Start typing to search timeline events"}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        Make sure patient data has been synced to Algolia using the "Sync to Algolia" button.
      </p>
    </div>
  );
}

// No results boundary
function NoResultsBoundary({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { results } = useInstantSearch();

  if (!results.__isArtificial && results.nbHits === 0) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// Main component props
interface AlgoliaInstantSearchProps {
  /** REQUIRED: Patient ID - used to determine patient-specific index */
  patientId: string;
  showFilters?: boolean;
  showPagination?: boolean;
  hitsPerPage?: number;
  onHitSelect?: (hit: any) => void;
  className?: string;
}

/**
 * Algolia InstantSearch component for patient-specific timeline search
 * Uses patient-specific indices: patient_timeline_<patientId>
 */
export function AlgoliaInstantSearch({
  patientId,
  showFilters = true,
  showPagination = true,
  hitsPerPage = 20,
  onHitSelect,
  className,
}: AlgoliaInstantSearchProps) {
  // Get patient-specific index name
  const indexName = useMemo(() => getPatientTimelineIndex(patientId), [patientId]);

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName} insights>
      <Configure hitsPerPage={hitsPerPage} />

      <div className={cn("w-full max-w-full space-y-4 overflow-hidden", className)}>
        {/* Search Box */}
        <div className="relative w-full max-w-full">
          <SearchBox
            placeholder="Search timeline events..."
            classNames={{
              root: "relative w-full max-w-full",
              form: "relative w-full max-w-full",
              input:
                "flex h-10 w-full max-w-full rounded-md border border-input bg-background px-3 py-2 pl-10 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              submit: "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10",
              reset: "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10",
              submitIcon: "h-4 w-4",
              resetIcon: "h-4 w-4",
            }}
          />
        </div>

        {/* Stats */}
        <div className="w-full">
          <SearchStats />
        </div>

        {/* Filters and Results */}
        <div className={cn("flex w-full max-w-full gap-4 lg:gap-6", showFilters ? "flex-col lg:flex-row" : "")}>
          {/* Filters Sidebar */}
          {showFilters && (
            <Card className="h-fit w-full shrink-0 lg:w-64 lg:max-w-[256px]">
              <CardContent className="p-4">
                <h3 className="mb-3 font-semibold">Filter by Type</h3>
                <RefinementList
                  attribute="type"
                  classNames={{
                    root: "space-y-2 w-full",
                    list: "space-y-1 w-full",
                    item: "flex items-center gap-2 w-full",
                    checkbox:
                      "h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700",
                    label: "flex cursor-pointer items-center gap-2 text-sm w-full min-w-0",
                    labelText: "capitalize truncate min-w-0",
                    count: "ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground",
                  }}
                />
              </CardContent>
            </Card>
          )}

          {/* Results */}
          <div className="w-full min-w-0 max-w-full flex-1 overflow-hidden">
            <NoResultsBoundary fallback={<NoResults />}>
              <div className="w-full max-w-full overflow-hidden">
                <Hits
                  hitComponent={({ hit }) => (
                    <div
                      onClick={() => onHitSelect?.(hit)}
                      className={cn(onHitSelect && "cursor-pointer", "w-full max-w-full")}
                    >
                      <TimelineHit hit={hit} />
                    </div>
                  )}
                  classNames={{
                    root: "space-y-3 w-full max-w-full",
                    list: "space-y-3 w-full max-w-full",
                  }}
                />
              </div>

              {/* Pagination */}
              {showPagination && (
                <div className="mt-6 w-full overflow-x-auto">
                  <div className="flex min-w-max justify-center">
                    <Pagination
                      classNames={{
                        root: "flex justify-center",
                        list: "flex items-center gap-1 flex-wrap",
                        item: "flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors shrink-0",
                        link: "flex h-full w-full items-center justify-center hover:bg-accent",
                        selectedItem: "bg-primary text-primary-foreground",
                        disabledItem: "opacity-50 cursor-not-allowed",
                        firstPageItem: "",
                        lastPageItem: "",
                        previousPageItem: "",
                        nextPageItem: "",
                        pageItem: "",
                      }}
                    />
                  </div>
                </div>
              )}
            </NoResultsBoundary>
          </div>
        </div>

        {/* Powered by Algolia */}
        <div className="w-full truncate text-center text-xs text-muted-foreground">
          <span className="inline-block max-w-full truncate">
            Searching patient index: <span className="font-mono text-[10px]">{indexName}</span> • Powered by Algolia
          </span>
        </div>
      </div>
    </InstantSearch>
  );
}

// Export a simpler inline search variant
export function AlgoliaInlineSearch({
  patientId,
  onHitSelect,
}: {
  /** REQUIRED: Patient ID for patient-specific index */
  patientId: string;
  onHitSelect?: (hit: any) => void;
}) {
  // Get patient-specific index name
  const indexName = useMemo(() => getPatientTimelineIndex(patientId), [patientId]);

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName} insights>
      <Configure hitsPerPage={10} />

      <div className="space-y-4">
        <SearchBox
          placeholder="Search with Algolia..."
          classNames={{
            root: "relative",
            form: "relative",
            input:
              "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            submit: "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground",
            reset: "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground",
            submitIcon: "h-4 w-4",
            resetIcon: "h-4 w-4",
          }}
        />

        <NoResultsBoundary fallback={<NoResults />}>
          <Hits
            hitComponent={({ hit }) => (
              <div onClick={() => onHitSelect?.(hit)} className={cn(onHitSelect && "cursor-pointer")}>
                <TimelineHit hit={hit} />
              </div>
            )}
            classNames={{
              root: "space-y-2",
              list: "space-y-2",
            }}
          />
        </NoResultsBoundary>
      </div>
    </InstantSearch>
  );
}
